<template>
  <div class="flex flex-col items-start gap-2">
    <TransitionGroup>
      <div
        v-for="(a, id) in [...modelActions, null]"
        :key="id"
        class="w-full flex gap-2"
      >
        <ListSelect
          class="min-w-[24ch] w-[26ch] tooltip"
          placeholder="Add action"
          :data-tip="a == null ? 'Select special key action' : a.info.tooltip"
          :clearable="true"
          :options="availableLabels"
          :modelValue="a == null ? undefined : a.info.label"
          @update:modelValue="(typ: string) => a == null ? actAdd(typ) : updateActionType(typ, id)"
        />

        <ListSelect
          v-if="a != null && a.info.choices != undefined"
          class="min-w-[18ch] grow"
          :class="choiceTooltip(a.currentChoice, a.info) != '' ? 'tooltip' : ''"
          :data-tip="choiceTooltip(a.currentChoice, a.info)"
          :clearable="false"
          :options="maybeCall(a.info.choices)"
          :modelValue="a.currentChoice"
          @update:modelValue="(c: string) => updateActionChoice(c, a.info, id)"
        />
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  isTrans,
  isLayer,
  isDefaultLayer,
  isCustomLedCycle,
  isCustomLedBrightness,
  isCustomMouseClick,
  isCustomMouseMove,
  isCustomMouseSensitivity,
  NonKeycodeAction,
  isCustomConsumer,
  isCustomFirmware,
  CONSUMER_KEYS,
  IGNORED_CONSUMER_KEYS,
} from "@/keyboard";
import { useKeyboardStore, useKeycapStore } from "@/stores";
import ListSelect from "@/components/utils/ListSelect.vue";

const kb = useKeyboardStore();
const kc = useKeycapStore();

const props = defineProps<{
  modelValue: NonKeycodeAction[];
}>();
const emit = defineEmits<{
  (_e: "update:modelValue", _value: NonKeycodeAction[]): void;
}>();

function maybeCall<T>(f: T | (() => T)): T {
  if (typeof f == "function") return (f as () => T)();
  return f;
}

type ActionInfo = {
  label: string;
  cond: (a: NonKeycodeAction) => boolean;
  multi?: boolean;
  choices?: string[] | (() => string[]);
  choiceProp?: string;
  choiceConvert?: (choice: string) => unknown;
  default: NonKeycodeAction | (() => NonKeycodeAction);
  tooltip: string;
  choiceTooltips?: Record<string, string>;
};

const consumer_keys = computed(() => {
  const basic = CONSUMER_KEYS.slice() as string[];
  const ignored = IGNORED_CONSUMER_KEYS.slice() as string[];
  return kc.advanced_consumer_keys ? basic.concat(ignored) : basic;
});

const CHOICES = computed(() => ({
  layers: () => kb.availableLayers.map(String),
  cycle: ["Up", "Down"],
  brightness: ["Up", "Down"],
  click: ["Left", "Mid", "Right"],
  move: [
    "Up",
    "Down",
    "Left",
    "Right",
    "WheelUp",
    "WheelDown",
    "PanLeft",
    "PanRight",
  ],
  sensitivity: ["Up", "Down"],
  consumer: consumer_keys.value.slice(), // convert tuple to string[]
  firmware: ["AllowBootloader", "JumpToBootloader", "Reboot", "InfiniteLoop"],
}));

const ACTIONS = computed((): ActionInfo[] => [
  {
    label: "Transparent",
    cond: isTrans,
    default: "Trans",
    tooltip:
      "This key will use action from the current default layer (by default layer 0).",
  },
  {
    label: "Layer",
    cond: isLayer,
    choices: CHOICES.value.layers,
    choiceProp: "Layer",
    choiceConvert: (v) => Number(v),
    default: () => ({
      Layer: Math.min(1, kb.layerCount - 1),
    }),
    tooltip:
      "While pressed, changes current layer, making all other keys use actions from the specified layer.",
  },
  {
    label: "Default Layer",
    cond: isDefaultLayer,
    choices: CHOICES.value.layers,
    choiceProp: "DefaultLayer",
    choiceConvert: (v) => Number(v),
    default: () => ({
      DefaultLayer: Math.min(1, kb.layerCount - 1),
    }),
    tooltip:
      "After pressing changes the default layer. To restore the previous default layer another key with this action must be used.",
  },
  {
    label: "Led Cycle",
    cond: isCustomLedCycle,
    choices: CHOICES.value.cycle,
    choiceProp: "Cycle",
    default: { Cycle: "Up" },
    tooltip: "Cycle through available LED configurations.",
  },
  {
    label: "Led Brightness",
    cond: isCustomLedBrightness,
    choices: CHOICES.value.brightness,
    choiceProp: "Brightness",
    default: { Brightness: "Up" },
    tooltip: "Change LED brightness",
  },
  {
    label: "Mouse Click",
    cond: isCustomMouseClick,
    choices: CHOICES.value.click,
    choiceProp: "Click",
    default: { Click: "Left" },
    multi: true,
    tooltip: "Emulate a mouse click.",
    choiceTooltips: {
      Left: "Left mouse button",
      Mid: "Middle mouse button",
      Right: "Right mouse button",
    },
  },
  {
    label: "Mouse Move",
    cond: isCustomMouseMove,
    choices: CHOICES.value.move,
    choiceProp: "Move",
    default: { Move: "Up" },
    multi: true,
    tooltip: "Emulate mouse movement while this key is being pressed.",
    choiceTooltips: {
      Up: "Move up",
      Down: "Move down",
      Left: "Move left",
      Right: "Move right",
      WheelUp: "Scroll up with mouse wheel",
      WheelDown: "Scroll down with mouse wheel",
      PanLeft: "Pan (scroll) left",
      PanRight: "Pan (scroll) right",
    },
  },
  {
    label: "Mouse Sensitivity",
    cond: isCustomMouseSensitivity,
    choices: CHOICES.value.sensitivity,
    choiceProp: "Sensitivity",
    default: { Sensitivity: "Up" },
    tooltip: "Change mouse movement emulation sensitivity.",
  },
  {
    label: kc.advanced_consumer_keys ? "Consumer" : "Media",
    cond: isCustomConsumer,
    choices: CHOICES.value.consumer,
    choiceProp: "Consumer",
    multi: true,
    default: { Consumer: "ScanNextTrack" },
    tooltip: "Control media playback.",
  },
  {
    label: "Firmware",
    cond: isCustomFirmware,
    choices: CHOICES.value.firmware,
    choiceProp: "Firmware",
    default: { Firmware: "AllowBootloader" },
    tooltip: "Trigger special firmware-related action.",
    choiceTooltips: {
      AllowBootloader:
        'Allow jumping to bootloader to flash new firmware. Required if "Bootload secure mode" is on.',
      JumpToBootloader:
        "Switch to bootloader and wait for new firmware. All other keys will stop working when this action is used! This may not work on some systems.",
      Reboot:
        "Reset firmware as if the device has been unplugged and plugged again.",
      InfiniteLoop:
        "Start infinite loop to test if keyboard can correctly recover from critical errors. Only useful for debugging.",
    },
  },
]);

const currentChoice = (a: NonKeycodeAction, i: ActionInfo) => {
  if (i.choices == undefined && i.choiceProp == undefined) return undefined;
  else if (i.choices != undefined && i.choiceProp != undefined)
    return (a as { [key: string]: string })[i.choiceProp];
  else throw Error("Both choices and choiceProp must be defined");
};

const choiceTooltip = (c: string | undefined, i: ActionInfo) => {
  if (c == undefined || i.choiceTooltips == undefined) return "";
  return i.choiceTooltips[c] ?? "";
};

const modelActions = computed(() =>
  props.modelValue.map((a) => {
    const info = ACTIONS.value.find((i) => i.cond(a));
    if (info == undefined) throw Error("Unknown action type: " + a);
    return { action: a, info, currentChoice: currentChoice(a, info) };
  })
);

const availableLabels = computed(() => {
  const opts: string[] = [];
  ACTIONS.value.forEach((info) => {
    if (info.multi || props.modelValue.filter(info.cond).length == 0)
      opts.push(info.label);
  });
  return opts;
});

const defaultForLabel = (label: string): NonKeycodeAction => {
  const info = ACTIONS.value.find((i) => i.label == label);
  if (info == undefined) throw Error("Unknown ActCustomFlat type: " + label);
  return maybeCall(info.default);
};

const updateAction = (id: number, act: NonKeycodeAction) => {
  if (JSON.stringify(act) == JSON.stringify(props.modelValue[id])) return;
  const newActions = [...props.modelValue];
  newActions[id] = act;
  emit("update:modelValue", newActions);
};

const updateActionChoice = (choice: string, info: ActionInfo, id: number) => {
  if (info.choices == undefined || info.choiceProp == undefined)
    throw Error(`Undefined choices for ${info.label}`);

  const act = props.modelValue[id];
  const current = (act as { [key: string]: string })[info.choiceProp];
  if (current == undefined)
    throw Error(`Property ${info.choiceProp} does not exist in ${info.label}`);

  const value =
    info.choiceConvert != null ? info.choiceConvert(choice) : choice;
  const newAction = Object.assign({}, act, { [info.choiceProp]: value });
  updateAction(id, newAction);
};

const actAdd = (typ?: string) => {
  const act = typ != undefined ? defaultForLabel(typ) : "Trans";
  emit("update:modelValue", [...props.modelValue, act]);
};

const actDelete = (i: number) => {
  const actions = [...props.modelValue];
  actions.splice(i, 1);
  emit("update:modelValue", [...actions]);
};

const updateActionType = (typ: string | null, id: number) => {
  // TODO: action cache
  // Delete on de-select, else create default action for the new type
  if (typ == null) actDelete(id);
  else updateAction(id, defaultForLabel(typ));
};
</script>

<style scoped>
.v-move,
.v-enter-active,
.v-leave-active {
  transition: all 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
